import React, { useContext, useState } from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { OpenInNewOutlined, WarningAmberOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { PRIMARY } from '../constants';
import { PreviewCtx } from '../contexts';
import { useSite } from '../../../context/SiteContext';

// ─── External link dialog ─────────────────────────────────────────────────────
function ExternalLinkDialog({ url, open, onConfirm, onCancel }) {
  return (
    <Dialog open={open} onClose={onCancel} onClick={e => e.stopPropagation()}
      PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 24px 64px rgba(0,0,0,0.18)', maxWidth: 400, width: '100%', p: 0.5 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 0.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 2, background: alpha('#f59e0b', 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <WarningAmberOutlined sx={{ fontSize: 18, color: '#f59e0b' }} />
        </Box>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#1a1a2e' }}>Visit external link?</Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 0.5 }}>
        <Typography sx={{ fontSize: '0.82rem', color: '#5a6474', mb: 1 }}>You're about to leave this page and visit:</Typography>
        <Box sx={{ px: 1.5, py: 1, background: '#f0f4f8', borderRadius: 1.5, border: '1px solid #e4e9f0', wordBreak: 'break-all' }}>
          <Typography sx={{ fontSize: '0.78rem', fontFamily: 'monospace', color: PRIMARY }}>{url}</Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Button onClick={onCancel} variant="outlined" size="small"
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, borderColor: '#e4e9f0', color: '#5a6474', '&:hover': { borderColor: '#c4cdd8', background: '#f8f9fb' } }}>
          Cancel
        </Button>
        <Button onClick={onConfirm} variant="contained" size="small" startIcon={<OpenInNewOutlined sx={{ fontSize: 14 }} />}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, background: PRIMARY, boxShadow: 'none', '&:hover': { background: '#0d62a0', boxShadow: 'none' } }}>
          Visit link
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Footer link ──────────────────────────────────────────────────────────────
function FooterLink({ link, color, hoverColor, onExternalConfirm }) {
  const [hovered, setHovered] = useState(false);
  const type = link.type || 'external';
  const href = link.href || '';

  const handleClick = (e) => {
    e.stopPropagation();
    if (type === 'section') {
      const sectionId = href.replace('#section-', '');
      const el = document.getElementById(`section-${sectionId}`) || document.querySelector(`[data-section-id="${sectionId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (type === 'page') { if (href) window.location.href = href; return; }
    if (href) onExternalConfirm(href, !!link.newTab);
  };

  return (
    <Typography onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{ fontSize: '0.875rem', fontWeight: 500, color: hovered ? (hoverColor || PRIMARY) : (color || '#5a6474'), cursor: 'pointer', whiteSpace: 'nowrap', transition: 'color 0.15s', textDecoration: hovered ? 'underline' : 'none' }}>
      {link.label || 'Link'}
    </Typography>
  );
}

// ─── Logo area ────────────────────────────────────────────────────────────────
function LogoArea({ config, onLogoClick }) {
  const logoColor = config.style?.logoColor || '#1a1a2e';
  const clickSx = onLogoClick ? { cursor: 'pointer', '&:hover': { opacity: 0.8 }, transition: 'opacity 0.15s' } : {};
  if (config.logoSrc) {
    return (
      <Box onClick={onLogoClick} sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, ...clickSx }}>
        <Box component="img" src={config.logoSrc} alt={config.logoAlt || 'Logo'}
          sx={{ height: config.logoHeight || 32, width: 'auto', display: 'block', objectFit: 'contain' }} />
      </Box>
    );
  }
  return (
    <Box onClick={onLogoClick} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0, ...clickSx }}>
      <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: alpha(PRIMARY, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ width: 12, height: 12, borderRadius: '50%', background: PRIMARY }} />
      </Box>
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: logoColor, letterSpacing: '-0.5px' }}>
        {config.logoText || 'Event'}
      </Typography>
    </Box>
  );
}

// ─── Three Columns template ───────────────────────────────────────────────────
function ColumnsFooter({ config, onExternalConfirm, breakpoint, onLogoClick }) {
  const isMobile  = breakpoint === 'mobile';
  const links     = config.links || [];
  const bg        = config.style?.background     || '#ffffff';
  const linkColor = config.style?.linkColor      || '#5a6474';
  const linkHover = config.style?.linkHoverColor || PRIMARY;
  const copyColor = config.style?.copyrightColor || '#9ea8b3';
  const border    = config.style?.borderColor    || '#e8edf2';
  const px        = config.style?.paddingX       || '48px';
  const py        = config.style?.paddingY       || '40px';
  const copyright = config.copyrightText || `© ${new Date().getFullYear()} My Company. All rights reserved.`;

  return (
    <Box sx={{ width: '100%', background: bg, borderTop: `1px solid ${border}`, boxSizing: 'border-box', display: 'flex', justifyContent: 'center' }}>
      <Box sx={{
        px, py, boxSizing: 'border-box',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: 'flex-start',
        justifyContent: isMobile ? 'flex-start' : 'space-between',
        gap: isMobile ? 3 : 4,
        width: '100%',
        maxWidth: 1100
      }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
          <LogoArea config={config} onLogoClick={onLogoClick} />
          {config.tagline && (
            <Typography sx={{ fontSize: '0.8rem', color: linkColor, maxWidth: 200, lineHeight: 1.5 }}>
              {config.tagline}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', flex: isMobile ? 'none' : 1, justifyContent: isMobile ? 'flex-start' : 'center' }}>
          {links.length > 0 && links.map((link, i) => <FooterLink key={link.id || i} link={link} color={linkColor} hoverColor={linkHover} onExternalConfirm={onExternalConfirm} />)}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: isMobile ? 'flex-start' : 'flex-end', minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.78rem', color: copyColor, textAlign: isMobile ? 'left' : 'right' }}>
            {copyright}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Centered Stack template ──────────────────────────────────────────────────
function CenteredFooter({ config, onExternalConfirm, onLogoClick }) {
  const links     = config.links || [];
  const bg        = config.style?.background     || '#ffffff';
  const linkColor = config.style?.linkColor      || '#5a6474';
  const linkHover = config.style?.linkHoverColor || PRIMARY;
  const copyColor = config.style?.copyrightColor || '#9ea8b3';
  const border    = config.style?.borderColor    || '#e8edf2';
  const px        = config.style?.paddingX       || '48px';
  const py        = config.style?.paddingY       || '40px';
  const copyright = config.copyrightText || `© ${new Date().getFullYear()} My Company. All rights reserved.`;

  return (
    <Box sx={{ width: '100%', background: bg, borderTop: `1px solid ${border}`, boxSizing: 'border-box' }}>
      <Box sx={{ px, py, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2.5, boxSizing: 'border-box' }}>
        <LogoArea config={config} onLogoClick={onLogoClick} />
        {config.tagline && (
          <Typography sx={{ fontSize: '0.85rem', color: linkColor, textAlign: 'center', maxWidth: 420, lineHeight: 1.6 }}>
            {config.tagline}
          </Typography>
        )}
        {links.length > 0 && <Box sx={{ width: 40, height: 1, background: border }} />}
        {links.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
            {links.map((link, i) => <FooterLink key={link.id || i} link={link} color={linkColor} hoverColor={linkHover} onExternalConfirm={onExternalConfirm} />)}
          </Box>
        )}
        <Typography sx={{ fontSize: '0.78rem', color: copyColor, textAlign: 'center' }}>{copyright}</Typography>
      </Box>
    </Box>
  );
}

// ─── Chip label ───────────────────────────────────────────────────────────────
function ChipLabel({ selected }) {
  return (
    <Box sx={{
      position: 'absolute', top: 0, left: 0, zIndex: 20,
      px: 0.9, py: 0.2,
      background: selected ? PRIMARY : alpha(PRIMARY, 0.12),
      borderRadius: '0 0 4px 0',
      fontSize: '0.58rem', fontWeight: 700,
      color: selected ? '#fff' : PRIMARY,
      letterSpacing: '0.5px', textTransform: 'uppercase',
      pointerEvents: 'none', transition: 'all 0.15s',
    }}>
      Footer
    </Box>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────
export function FooterSection({ section, config: configProp, selected, onSelectFooter, breakpoint = 'desktop' }) {
  const isPreview = useContext(PreviewCtx);
  const config    = configProp || section.footerConfig || {};
  const template  = config.template || 'columns';

  const navigate = useNavigate();
  const { event } = useSite();
  const onLogoClick = isPreview ? () => {
    const homePath = event?.urlMode === 'flat' ? '/' : `/${event?.slug || ''}`;
    navigate(homePath);
  } : undefined;

  const [externalDialog, setExternalDialog] = useState(null);
  const handleExternalConfirm = (url, newTab) => setExternalDialog({ url, newTab });
  const confirmVisit = () => {
    if (externalDialog?.url) window.open(externalDialog.url, externalDialog.newTab ? '_blank' : '_self', 'noopener,noreferrer');
    setExternalDialog(null);
  };

  const inner = template === 'centered'
    ? <CenteredFooter config={config} onExternalConfirm={handleExternalConfirm} onLogoClick={onLogoClick} />
    : <ColumnsFooter  config={config} onExternalConfirm={handleExternalConfirm} breakpoint={breakpoint} onLogoClick={onLogoClick} />;

  if (isPreview) {
    return (
      <>
        {inner}
        <ExternalLinkDialog open={!!externalDialog} url={externalDialog?.url || ''} onConfirm={confirmVisit} onCancel={() => setExternalDialog(null)} />
      </>
    );
  }

  const selectionSx = {
    outline: selected ? `2px solid ${PRIMARY}` : '2px solid transparent',
    outlineOffset: -2,
    transition: 'outline-color 0.15s',
    '&:hover': { outline: `2px solid ${selected ? PRIMARY : alpha(PRIMARY, 0.45)}` },
  };

  return (
    <>
      <Box onClick={e => { e.stopPropagation(); onSelectFooter(section.id); }}
        sx={{ position: 'relative', cursor: 'pointer', userSelect: 'none', ...selectionSx }}>
        {inner}
        <ChipLabel selected={selected} />
      </Box>
      <ExternalLinkDialog open={!!externalDialog} url={externalDialog?.url || ''} onConfirm={confirmVisit} onCancel={() => setExternalDialog(null)} />
    </>
  );
}